<template>
  <Transition name="slide-up">
    <div
      v-if="visible"
      class="fixed inset-x-0 bottom-0 z-[9999] border-t border-[--color-neutral-a4] bg-[--color-additional-50] px-0 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.1)] md:px-4"
      role="alert"
      aria-live="polite"
    >
      <div :class="[containerClass, 'mx-auto']">
        <div class="flex flex-col items-center justify-between gap-4 md:flex-row">
          <!-- Left: Success Icon + Message + Cart Icon with Badge -->
          <div class="flex w-full items-center justify-between gap-3 md:w-auto md:justify-start md:gap-4">
            <div class="flex items-center gap-3 md:gap-4">
              <!-- Success Checkmark -->
              <div
                class="flex size-6 shrink-0 items-center justify-center rounded-full bg-[--color-alert-d0] md:size-8"
              >
                <VcIcon name="check" :size="20" class="text-[--color-white]" />
              </div>

              <!-- Message -->
              <div class="flex flex-col">
                <p class="text-base font-bold">{{ t("shared.cart.added_to_cart") }}</p>
                <p v-if="addedProductCode" class="text-sm font-medium capitalize text-[--color-neutral-a2]">
                  {{ addedProductCode }}
                </p>
              </div>
            </div>

            <!-- Cart Icon with Badge -->
            <div class="relative mx-2 shrink-0 md:mr-0">
              <VcIcon name="icon-cart-shopping-md" :size="26" />
              <span
                :class="[
                  'absolute -right-2 -top-2.5 flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-[--color-primary-500] p-1',
                  { 'cart-badge--pop': badgeAnimating },
                ]"
              >
                <span class="text-xs font-bold text-[--color-additional-50]">
                  {{ addedQuantity }}
                </span>
              </span>
            </div>
          </div>

          <!-- Right: Action Buttons -->
          <div class="flex w-full shrink-0 items-center justify-end gap-2 md:w-auto md:gap-3">
            <VcButton
              color="secondary"
              variant="outlineTL"
              size="lg"
              class="hidden flex-1 whitespace-nowrap text-sm font-medium md:block"
              @click="close"
            >
              {{ $t("common.buttons.continue_shopping") }}
            </VcButton>
            <VcButton
              type="button"
              color="primary"
              variant="solid"
              size="lg"
              to="/cart"
              class="flex-1 rounded bg-[--color-primary] px-4 py-2 text-sm font-bold capitalize text-[--color-additional-50] hover:bg-[--color-primary-hover] md:px-6"
              @click="handleViewCart"
            >
              {{ $t("common.buttons.view_cart") }}
            </VcButton>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { eventBus } from "@/shared/thorlabs/shared/event-bus";

const { t } = useI18n(); // eslint-disable-line @typescript-eslint/no-unused-vars
const breakpoints = useBreakpoints(breakpointsTailwind);
const isLg = breakpoints.greaterOrEqual("lg");
const containerClass = computed(() => (isLg.value ? "x-large-container" : "md:px-0 px-4"));

const visible = ref(false);
const addedQuantity = ref(0);
const badgeAnimating = ref(false);
const addedProductCode = ref("");
let hideTimeout: ReturnType<typeof setTimeout> | undefined;

const handleItemAddedToCart = (payload: { productCode?: string; quantity: number }) => {
  // Clear any existing timeout
  if (hideTimeout) {
    clearTimeout(hideTimeout);
  }

  // Update content
  addedProductCode.value = payload.productCode ?? "";
  addedQuantity.value = payload.quantity;

  // Show bar
  visible.value = true;

  // Trigger badge animation
  badgeAnimating.value = false;
  void document.body.offsetHeight; // Force reflow
  badgeAnimating.value = true;

  setTimeout(() => {
    badgeAnimating.value = false;
  }, 400);

  // Auto-hide after 5 seconds
  hideTimeout = setTimeout(() => {
    close();
  }, 10000);
};

const close = () => {
  visible.value = false;
  if (hideTimeout) {
    clearTimeout(hideTimeout);
  }
};

const handleViewCart = () => {
  close();
};

onMounted(() => {
  eventBus.on("cartItemAdded", handleItemAddedToCart);
});

onBeforeUnmount(() => {
  eventBus.off("cartItemAdded", handleItemAddedToCart);
  if (hideTimeout) {
    clearTimeout(hideTimeout);
  }
});
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease-out;
}

.slide-up-enter-from {
  transform: translateY(100%);
}

.slide-up-leave-to {
  transform: translateY(100%);
}

@keyframes cart-badge-pop {
  0% {
    transform: scale(1);
  }
  20% {
    transform: scale(1.8);
  }
  40% {
    transform: scale(0.9);
  }
  60% {
    transform: scale(1.3);
  }
  80% {
    transform: scale(0.95);
  }
  100% {
    transform: scale(1);
  }
}

.cart-badge--pop {
  animation: cart-badge-pop 0.4s ease;
}
</style>
